import type { I18n } from "@lingui/core"
import { msg } from "@lingui/core/macro"

const ERROR_PREFIX = /^(?:processing failed|recovery failed|error)\s*:\s*/i
const STACK_LINE = /^\s*at\s+\S/
const FILE_LOCATION = /(?:\(|\s)[\w./-]+\.(?:ts|tsx|js|jsx):\d+(?::\d+)?\)?/g

/**
 * Drop stack frames, file locations and the "Processing failed:" wrapper so a
 * matcher sees the reason, not the plumbing around it.
 */
function cleanMeetingError(raw: string): string {
  const kept = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !STACK_LINE.test(line) && !line.includes("node_modules"))
  let text = kept.join(" ").replace(FILE_LOCATION, " ")
  let previous = ""
  while (text !== previous) {
    previous = text
    text = text.replace(ERROR_PREFIX, "")
  }
  return text.replace(/\s+/g, " ").trim()
}

/**
 * A short sentence for `meeting.error`. Known failure shapes get a plain
 * sentence. Anything else — a stack, a provider dump, an unknown code —
 * becomes the same fallback so a card never shows a cut-off traceback.
 * Returns null when there is no error text, so the caller can name the
 * pipeline stage instead.
 */
export function describeMeetingError(raw: string | null | undefined, i18n: I18n): string | null {
  if (!raw?.trim()) return null
  const text = cleanMeetingError(raw)
  if (!text) return i18n._(msg`Something went wrong while processing.`)
  const lower = text.toLowerCase()

  if (
    lower.includes("no recoverable") ||
    lower.includes("no audio") ||
    lower.includes("no dialogue") ||
    lower.includes("no_audio")
  ) {
    return i18n._(msg`There was no audio to process.`)
  }
  if (lower.includes("ffmpeg") || lower.includes("could not decode") || lower.includes("corrupt")) {
    return i18n._(msg`The recording could not be read.`)
  }
  if (lower.includes("timeout") || lower.includes("timed out") || lower.includes("deadline")) {
    return i18n._(msg`Processing took too long.`)
  }
  if (lower.includes("credit") || lower.includes("payment required")) {
    return i18n._(msg`There weren't enough credits to finish.`)
  }
  if (
    lower.includes("no speech") ||
    lower.includes("empty transcript") ||
    lower.includes("no transcript") ||
    lower.includes("no_speech")
  ) {
    return i18n._(msg`No speech was found in the recording.`)
  }
  if (lower.includes("stale in_progress") || lower.includes("abandoned")) {
    return i18n._(msg`The recording stopped before it finished.`)
  }
  if (lower.includes("did not complete") || lower.includes("recovery failed")) {
    return i18n._(msg`Processing stopped before it finished.`)
  }
  return i18n._(msg`Something went wrong while processing.`)
}
